import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  defaultParams,
  PriceSourceUnreachableError,
  ProblemError,
  StaleVersionError,
  useExtraction,
  usePricingSource,
  type CaseDetailOut,
} from "@/api";
import { Button } from "@/components/Button";
import { CancelledNotice } from "@/components/CancelledNotice";
import { DocumentsButton } from "@/components/DocumentsButton";
import { EmptyState } from "@/components/EmptyState";
import { BoneEmptyState, Skeleton } from "@/components/PageSkeleton";
import { PageTitle } from "@/components/PageTitle";
import { StateMessage } from "@/components/StateMessage";
import { useScreenToast } from "@/components/Toast";
import { restorePath } from "@/lib/facadeEditability";
import { formatDateTime } from "@/lib/format";
import { noStandWay, recordOf } from "@/lib/noStand";

import { FROZEN_STATUSES } from "./CalculationScreen";
import styles from "./CalculationScreen.module.css";
import { RUN_STATUS_KEY, useRunCalculation } from "./useRunCalculation";

/** The defaults the first run is made with, in words — the same figures `defaultParams` sends (D-5, KF-14, KF-17). */
const DEFAULTS_SENTENCE =
  "Verzinkt, Tragstab 30x2, Masche 30/30 (Barfuß 25/11), ohne Rutschhemmung, Marge 30 %";

/**
 * A Vorgang nobody has priced yet (D-22). Opening writes nothing; the first
 * calculation is this one button. The mirror's status stands beside it —
 * its own sentence (`source.data.detail`, controller notes §4), not a
 * paraphrase written here — so a person sees before pressing whether there
 * is anything to price against.
 *
 * Two shapes never reach that button (fix round 2, items 6 and 15), both
 * checked before anything about the mirror or a run is read: a Vorgang the
 * server itself has frozen (`sent`/`won`/`lost` — a legacy Vorgang reached
 * this status through the pre-E-52 shortcut and so may never have had a
 * calculation at all) and a Vorgang with no Stand yet
 * (`latestVersionNo === 0` — `/umriss` → „Übernehmen" reaches this route
 * before a single wall is placed). Pressing „Kalkulation starten" on
 * either shape still worked, technically — it just posted straight into a
 * 409 (`_IS_SENT`, `routes/calculation.py`) or a 422 (`expected_version_no`
 * has `ge=1`) that named nothing a person could act on.
 */
export function EmptyCalculation({ caseId, detail }: { caseId: string; detail: CaseDetailOut }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useScreenToast();
  const run = useRunCalculation(caseId);
  const source = usePricingSource();
  const failure = run.error;
  const frozen = FROZEN_STATUSES.has(detail.status);
  const noStandYet = detail.latestVersionNo === 0;
  // Only with no Stand and a drawing on file is there a reading to ask about
  // (Ruling T7-1, R3-3).
  const readable = noStandYet && detail.documents.length > 0;
  const reading = useExtraction(readable ? caseId : null);
  const noStand = noStandYet ? noStandWay(caseId, reading.data?.status ?? null, "calculation") : null;
  const backToPlanning = (
    <Button variant="quiet" onClick={() => navigate(`/vorgang/${caseId}/planung`)}>
      Zurück zur Planung
    </Button>
  );

  return (
    <div className={styles.screen}>
      <header className={styles.head}>
        <PageTitle className={styles.title}>Kalkulation</PageTitle>
        <p className={styles.lede}>
          Jede Position mit Betrag, Basis und Preisquelle. Freigegeben wird von Hand.
        </p>
        {/* The Vorgang is named by the frame above this page (design
            2026-10-22 §5): the meta line says only the Stand. */}
        <p className={styles.meta}>
          {noStandYet ? (
            // Not „Stand 0" above a notice that says there is none (R3-3).
            "noch kein Stand"
          ) : (
            <>
              Stand <span className="num">{detail.latestVersionNo}</span>
            </>
          )}
        </p>
      </header>

      {detail.status === "cancelled" ? (
        // Storniert (V-1): „Kalkulation starten“ would only meet the 409 —
        // the notice the record shows, and the way back to it.
        <>
          <CancelledNotice
            cancellation={detail.cancellation ?? null}
            action={
              <Button
                variant="primary"
                aria-describedby="case-cancelled"
                onClick={() => navigate(restorePath(caseId))}
              >
                Wiederherstellen …
              </Button>
            }
          />
          <div className={styles.actions}>{backToPlanning}</div>
        </>
      ) : frozen ? (
        // Item 15: the real 409 sentence (`_IS_SENT`,
        // `routes/calculation.py`) rather than a paraphrase — a legacy
        // Vorgang here never got as far as a calculation, so there is
        // nothing this screen's usual "freigegeben, wie sie war" framing
        // could point at.
        <StateMessage title="Angebot ist raus">
          Der Vorgang ist versendet — Änderungen sind nicht mehr möglich.
          <div className={styles.actions}>
            {backToPlanning}
            <DocumentsButton caseId={caseId} versionNo={detail.latestVersionNo} />
          </div>
        </StateMessage>
      ) : noStandYet ? (
        // Item 6: `CaseDetailOut.latestVersionNo` is `0` for a case with no
        // plan version (`schema/service.py::latest_version_no`) — pressing
        // „Kalkulation starten" posted `expectedVersionNo: 0` into pydantic's
        // `ge=1` and came back a 422 that named nothing a person could act
        // on. No button that would only ever refuse. Round-3 screen check,
        // R3-3: and no way back to a board that has nothing to show either —
        // the cause that holds and the way on inside this Vorgang.
        readable && reading.isPending ? (
          // The empty state's place, in bones (Task 10, B-4).
          <Skeleton label="Vorgang wird geladen">
            <BoneEmptyState />
          </Skeleton>
        ) : noStand !== null ? (
          // Nothing to price yet, and the one way on — the empty state the
          // board's sibling page shows too (UX gate round 4, #11), not a
          // notice over an empty page.
          <EmptyState
            title={noStand.title}
            actions={
              <>
                <Button variant="primary" onClick={() => navigate(noStand.next.to)}>
                  {noStand.next.label}
                </Button>
                <Button variant="quiet" onClick={() => navigate(recordOf(caseId))}>
                  Zur Akte
                </Button>
              </>
            }
          >
            {noStand.text}
          </EmptyState>
        ) : null
      ) : (
        <>
          {failure instanceof PriceSourceUnreachableError ? (
            <StateMessage tone="conflict" title="Preisquelle nicht verfügbar">
              {failure.message} Ohne Preisstand rechnet nichts — bitte an aivori melden.
            </StateMessage>
          ) : failure instanceof StaleVersionError ? (
            <StateMessage tone="conflict" title="Stand veraltet">
              {`Stand ${failure.latestVersionNo} ist inzwischen der letzte — erneut starten rechnet gegen ihn.`}
            </StateMessage>
          ) : failure instanceof ProblemError ? (
            <StateMessage tone="conflict" title="Kalkulation nicht gestartet">
              {failure.userMessage}
            </StateMessage>
          ) : failure !== null ? (
            <StateMessage tone="conflict" title="Kalkulation nicht gestartet">
              {failure.message}
            </StateMessage>
          ) : null}

          <StateMessage title="Noch keine Kalkulation.">
            {`Stand ${detail.latestVersionNo} wird mit den Preislisten bewertet — ${DEFAULTS_SENTENCE}. Alles davon lässt sich danach ändern.`}
            <p className={styles.sourceLine}>
              {source.data?.available && source.data.syncedAt !== null
                ? `Preisstand vom ${formatDateTime(source.data.syncedAt)}`
                : source.data && !source.data.available
                  ? source.data.detail
                  : null}
            </p>
            <div className={styles.actions}>
              <Button
                variant="primary"
                busy={run.isPending}
                disabled={run.isPending}
                onClick={() =>
                  run.mutate(
                    { expectedVersionNo: detail.latestVersionNo, ...defaultParams(detail.gratingType) },
                    {
                      // The route swaps this screen out for `CalculationScreen`
                      // the instant this lands — before the live region below
                      // could ever announce anything (S-3). Left in the cache
                      // for `CalculationRoute`'s own persistent status region
                      // to read instead (fix round 2, item 8) — it never
                      // unmounts across the swap, this component does.
                      onSuccess: ({ calc }) => {
                        const sentence = `Kalkulation ${calc.calcNo} gerechnet.`;
                        client.setQueryData(RUN_STATUS_KEY(caseId), sentence);
                        // Seen where it was pressed (Welle 1, idea 23); heard
                        // from the route's region, so for the eye only. The
                        // provider is the shell's: it outlives this screen.
                        toast.show({ text: sentence, announce: false });
                      },
                    },
                  )
                }
              >
                Kalkulation starten
              </Button>
              {backToPlanning}
              <DocumentsButton caseId={caseId} versionNo={detail.latestVersionNo} />
            </div>
          </StateMessage>

          <p role="status" className={styles.live}>
            {run.isPending ? "Rechnet …" : ""}
          </p>
        </>
      )}
    </div>
  );
}
